import { Typography } from "@mui/material";
import React, { useState } from "react";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import cn from "classnames";
import { useMutation } from "@tanstack/react-query";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslate } from "../../../../../hooks/useTranslate";
import IconStripeLogo from "../../../../../assets/images/icons/stripe-logo.svg?react";
import BaseDialog from "../../../../../components/BaseDialog";
import {
    isUKDevelopment,
    isLocal,
} from "../../../../../services/serviceConfig";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import { isVersionUK, isVersionUS } from "../../../../../services/versionService";
import TextInput from "../../../../../components/TextInput";
import { useToast } from "../../../../../context/toast";
import { useStudents } from "../../../../../context/students";
import axiosManager from "../../../../../services/axiosManager";
import Preloader from "../../../../../components/Preloader";
import "./PaymentSummary.css";

const currency = isVersionUK ? "GBP" : "USD";

const PaymentSummary = (props) => {
    const {
        subtext,
        onePaymentSum,
        techFee,
        discounts,
        total,
        deposit,
        studentFullName,
        onProceed,
        reload,
    } = props;

    const { t } = useTranslate();
    const toast = useToast();
    const { activeStudentId } = useStudents();

    const [discountCode, setDiscountCode] = useState("");

    const {
        mutateAsync: applyCode, isPending: isApplyingCode,
    } = useMutation({
        mutationFn: () => axiosManager.post$(`/parent/api/students/${activeStudentId}/PromotionCodes/${discountCode}`, {}).then((response) => response?.data),
        onError: (err) => toast.error(err.status === 400 ? err.response?.data ?? t("common.errorMessage") : t("common.errorMessage")),
        onSuccess: () => reload?.(),
    });

    const {
        mutateAsync: onDiscountRemove, isPending: isRemovingCode,
    } = useMutation({
        mutationFn: (discount) => axiosManager.delete$(`/parent/api/students/${activeStudentId}/PromotionCodes/${discount}`).then((response) => response?.data),
        onError: () => toast.error(t("common.errorMessage")),
        onSuccess: () => reload?.(),
    });

    const onApplyCode = async () => {
        try {
            await applyCode();
        } catch {
            // Error is in Mutation
        }
    };
    const onRemoveCode = async (discount) => {
        try {
            await onDiscountRemove(discount?.promoCode?.code);
        } catch {
            // Error is in Mutation
        }
    };

    return (
        <Preloader isLoading={isApplyingCode || isRemovingCode} overflow showInitiallyOverflow>
            <div className="payment-summary">
                <Typography variant="h4" component="div" className="text-primary">
                    {t("tuitionBilling.paymentSummary")}
                </Typography>
                {deposit > 0 && (
                    <div className="flex align-center justify-between gap-4 mt-4 mb-4">
                        <Typography variant="h4" component="div" className="text-primary payment-summary__tuition-text">
                            {t("tuitionBilling.refundableDeposit")}
                        </Typography>
                        <div className="text-align-right">
                            <Typography
                                variant="h4"
                                component="div"
                                className="text-primary payment-summary__tuition-summ"
                            >
                                {formatCurrency(currency).format(deposit)}
                            </Typography>
                        </div>
                    </div>
                )}
                <div className="flex align-center justify-between gap-4 mt-4 mb-4">
                    <div>
                        <Typography variant="h4" component="div" className="text-primary payment-summary__tuition-text">
                            {t("tuitionBilling.tuitionFor", { studentFullName })}
                        </Typography>
                        <Typography variant="subtitle2" component="div" className="">
                            {subtext}
                        </Typography>
                    </div>
                    <div className="text-align-right">
                        <Typography variant="h4" component="div" className="text-primary payment-summary__tuition-summ">
                            {formatCurrency(currency).format(onePaymentSum)}
                        </Typography>
                    </div>
                </div>
                <div className="payment__note-total">
                    <InfoOutlinedIcon className="payment__note-total-icon" />
                    <Typography variant="body1" component="div">
                        <b>{t("tuitionBilling.note")}</b>
                        {" "}
                        {t("tuitionBilling.noteTotal")}
                    </Typography>
                </div>
                <div className="divider mt-4 mb-8" />
                <div className="discounts-block">
                    <div className="flex align-end gap-4 add-student__apply-code mb-8">
                        <TextInput
                            label={t("addStudent.discountCode")}
                            placeholder={t("addStudent.discountCodePlaceholder")}
                            name="discountCode"
                            value={discountCode}
                            disabled={isApplyingCode}
                            onChange={(e) => setDiscountCode(e.target.value)}
                        />
                        <Button
                            className="button-primary"
                            loading={isApplyingCode}
                            onClick={onApplyCode}
                        >
                            {t("addStudent.applyCode")}
                        </Button>
                    </div>
                    {discounts?.length > 0 && (
                        <div className="discounts-block__applied">
                            <Typography variant="body1" component="div">{t("addStudent.discountCodeTitle")}</Typography>
                            {discounts.map((discount) => (
                                <div key={discount.id} className="discounts-block__applied__code">
                                    {`${discount.code}`}
                                    {discount.promoCode ? (
                                        <IconButton aria-label="close" onClick={() => onRemoveCode(discount)} disableRipple>
                                            <CloseIcon />
                                        </IconButton>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {/* <div className="divider mb-8" />
                <div className="flex align-center justify-between gap-4 mb-8">
                    <div>
                        <Typography variant="h4" component="div" className="text-primary payment-summary__total-text">
                            {t("tuitionBilling.totalToPayNow")}
                        </Typography>
                    </div>
                    <div className="text-align-right">
                        <Typography variant="h4" component="div" className="text-primary payment-summary__tuition-summ">
                            {formatCurrency(currency).format(total)}
                        </Typography>
                    </div>
                </div> */}
                <div className="flex align-center justify-between gap-4 mb-2">
                    <div>
                        <IconStripeLogo />
                    </div>
                    {!(isUKDevelopment || isLocal) && (
                        <div className="text-align-right">
                            <Typography variant="h5" component="div" className="text-warning">
                                {t("tuitionBilling.taxesCalculatedAtCheckout")}
                            </Typography>
                        </div>
                    )}
                </div>
                <Button
                    variant="contained"
                    className={cn("mt-2", isVersionUS ? "button-primary" : "button-accent", "student-details-form__button")}
                    onClick={onProceed}
                >
                    {t("tuitionBilling.proceedToPayment")}
                </Button>
            </div>
        </Preloader>
    );
};

export default PaymentSummary;
